/**
 * Registry of pure, CPU-bound tasks that may run in a worker process.
 *
 * **Every task here MUST be a pure function of its JSON-serializable payload:**
 * no live Playwright page, no shared mutable state, no I/O. The worker process
 * has none of those things — it only has this registry. Page-bound work
 * (ariaSnapshot, per-ref resolve, locator probing) stays on the host thread and
 * must not be added here.
 *
 * Inputs and outputs cross a process boundary, so both must survive
 * `JSON.stringify`/`parse` — note `format-snapshot` below carries
 * `elementHints` as entries (not a `Map`) and RETURNS `matchedHintIds` rather
 * than mutating an out-param, because neither a `Map` nor an in-place mutation
 * survives the boundary.
 *
 * Payload sizes vary by orders of magnitude: a snapshot substrate is ~90 KB,
 * while `image-op` carries a base64 screenshot that can reach ~9 MB each way.
 * Both are plain JSON strings, so no binary transport is needed and the 64 MB
 * `MAX_FRAME_BYTES` corruption tripwire still sits well above either. **But the
 * framing is not free at that size:** `FrameDecoder.push` reallocates the whole
 * accumulated buffer per stdio chunk, so decode cost grows super-linearly —
 * measured ~0.2 ms for a 90 KB substrate, ~7 ms at 3 MB, ~46 ms at 9 MB, and
 * that work lands on the HOST loop. Still a large net win (the same 9 MB image
 * costs 737–831 ms inline), but framing is now the dominant residual host cost
 * of an offloaded image, and a ring-buffer/offset-cursor `FrameDecoder` is the
 * fix if payloads grow much beyond this.
 *
 * @module cpu-offload/tasks
 */

import { runImageOpCaptured, type ImageOpOutcome, type ImageOpRequest } from '../image-ops.js';
import { formatSemanticSnapshot } from '../snapshot-formatter-render.js';
import type {
  ElementHintAnnotation,
  SemanticSnapshotOptions,
} from '../snapshot-formatter-shared.js';
import { appendV2FrameSelectors } from '../playwright-client/frame-walker.js';
import { appendSubstrateV2DerivedAttrs } from '../playwright-client/snapshot-substrate-v2.js';

/**
 * Payload for `augment-tail`: the synchronous pure post-passes that run at the
 * END of substrate augmentation, after the last page-bound step (frame-selector
 * collection). All are pure string transforms over the YAML, so they can run off
 * the host event loop. The page-bound input (frame selectors) is computed on the
 * host and passed in as serializable data.
 */
export interface AugmentTailPayload {
  yaml: string;
  /** `Map<ref, cssSelector>` flattened to entries for the wire. */
  frameSelectors?: Array<[string, string]>;
}

export interface AugmentTailResult {
  yaml: string;
}

/**
 * Pure implementation of the augmentation tail, shared by the offload task body
 * and the inline fallback so both produce byte-identical output.
 */
export function applyAugmentTail(p: AugmentTailPayload): AugmentTailResult {
  let yaml = p.yaml;
  if (p.frameSelectors && p.frameSelectors.length > 0) {
    yaml = appendV2FrameSelectors(yaml, new Map(p.frameSelectors));
  }
  yaml = appendSubstrateV2DerivedAttrs(yaml);
  return { yaml };
}

/**
 * Payload for `format-snapshot`. `options` is {@link SemanticSnapshotOptions}
 * minus the two fields that don't survive serialization:
 * - `elementHints` (a `Map`) is carried as `elementHints` entries and rebuilt;
 * - `matchedHintIds` is an out-param, so it's returned in the result instead.
 */
export interface FormatSnapshotPayload {
  yaml: string;
  options?: Omit<SemanticSnapshotOptions, 'elementHints' | 'matchedHintIds'>;
  /** `Map<hash, annotation>` flattened to entries for the wire. */
  elementHints?: Array<[string, ElementHintAnnotation]>;
}

export interface FormatSnapshotResult {
  text: string;
  /** Hints that matched in this snapshot — the host threads these back. */
  matchedHintIds?: string[];
}

/**
 * Pure tasks here are synchronous. The shared task shape also permits a Promise
 * because the child-only filesystem registry performs backpressured async I/O.
 * Either kind is supervised as one call; if it spins or wedges, the host
 * reclaims the whole worker via SIGKILL (Bun's `worker_threads.terminate()`
 * cannot interrupt synchronous JS — see the cpu-offload pool docs), which is
 * why these run in a process, not a thread.
 */
export type CpuTask<P = unknown, R = unknown> = (payload: P) => R | Promise<R>;

export const TASKS: Record<string, CpuTask> = {
  /**
   * Render a captured substrate YAML into the semantic snapshot string. This is
   * the canonical offload target: a pure projection of substrate with no page
   * access (enforced by snapshot-formatter-purity.test.ts).
   */
  'format-snapshot': ((p: FormatSnapshotPayload): FormatSnapshotResult => {
    const options: SemanticSnapshotOptions = { ...p.options };
    if (p.elementHints) {
      options.elementHints = new Map(p.elementHints);
    }
    const text = formatSemanticSnapshot(p.yaml, options);
    // `formatSemanticSnapshot` populates `options.matchedHintIds` in place;
    // return it so the host can re-attach it (the mutation can't cross back).
    return { text, matchedHintIds: options.matchedHintIds };
  }) as CpuTask,

  /**
   * Apply the pure tail of substrate augmentation (frame selectors + v2
   * derived attrs). Pure string transforms — no page access —
   * so they run off the host event loop under load. Output is identical to the
   * inline path (see applyAugmentTail).
   */
  'augment-tail': ((p: AugmentTailPayload): AugmentTailResult => applyAugmentTail(p)) as CpuTask,

  /**
   * Run one pure Jimp image operation (downsample / crop / highlight / annotate /
   * grid). One task rather than five, because the five differ only in their
   * payload shape — a single wire shape keeps one wrapper and one fallback
   * policy on the host. Every option arrives pre-resolved (see image-ops), so the
   * child makes no decisions and its output matches the inline path exactly.
   *
   * Returns its own failure as data rather than throwing, so a bad *image* is not
   * mistaken for a bad *worker* — see {@link ImageOpOutcome}.
   */
  'image-op': ((p: ImageOpRequest): Promise<ImageOpOutcome> => runImageOpCaptured(p)) as CpuTask,

  /**
   * Trivial liveness probe. Registered UNCONDITIONALLY (unlike the test-only
   * `__sum`/`__hang`/`__crash` below) so the production boot smoke-test has a
   * task to round-trip — it proves a child can spawn, complete the READY
   * handshake, and exchange a framed message. Pure constant; no inputs, so it is
   * harmless even if dispatched by hostile input.
   */
  __ping: (() => ({ ok: true })) as CpuTask,
};

// ── Internal diagnostics (prefixed `__`) ───────────────────────────────────
// Used by the pool's own tests to exercise the harness deterministically. Kept
// OUT of the production bundle (registered only when NODE_ENV !== 'production')
// so they can never be dispatched as an accidental or hostile pool input.
// (`__ping` above is the deliberate exception — the always-on boot probe.)
if (process.env.NODE_ENV !== 'production') {
  /** Sum 0..n-1 — a cheap, verifiable round-trip probe. */
  TASKS['__sum'] = ((p: { n: number }) => {
    let s = 0;
    for (let i = 0; i < p.n; i++) s += i;
    return s;
  }) as CpuTask;

  /** Spin forever — exercises terminate-on-timeout (host SIGKILLs the worker). */
  TASKS['__hang'] = (() => {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      /* never yields — only SIGKILL reclaims this */
    }
  }) as CpuTask;

  /** Crash the worker mid-call — exercises the crash (non-timeout) reject path. */
  TASKS['__crash'] = (() => {
    process.exit(1);
  }) as CpuTask;
}
